import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getMyPlans, createPlan, getPlan, updatePlan, archivePlan,
  addDay, deleteDay, updateDay, addExercise, updateExercise, deleteExercise,
  reorderExercises, reorderDays,
  createAssignment, updateAthletesPlan,
} from '../../services/endpoints/trainingService';
import type {
  TrainingPlanSummary, TrainingPlanDetail, TrainingDay, TrainingExercise,
  CreateTrainingExerciseForm,
} from '../../types/training';
import { getExerciseLibrary } from '../../services/endpoints/exerciseLibraryService';
import type { ExerciseLibraryEntry } from '../../services/endpoints/exerciseLibraryService';
import ExercisePicker from '../../components/training/ExercisePicker';

// ── Exercise library module-level cache ──────────────────────────────────────
let _libCache: ExerciseLibraryEntry[] | null = null;
let _libPromise: Promise<ExerciseLibraryEntry[]> | null = null;
const getLibraryCached = (): Promise<ExerciseLibraryEntry[]> => {
  if (_libCache) return Promise.resolve(_libCache);
  if (!_libPromise) {
    _libPromise = getExerciseLibrary().then(d => { _libCache = d; return d; }).catch(() => []);
  }
  return _libPromise;
};

// ── Tiny helpers ───────────────────────────────────────────────────────────────
const InlineConfirm: React.FC<{
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}> = ({ message, confirmLabel = 'Yes', onConfirm, onCancel, danger = true }) => (
  <span className="inline-flex items-center gap-2 text-sm">
    <span className={danger ? 'text-red-400' : 'text-gray-300'}>{message}</span>
    <button
      onClick={onConfirm}
      className={`px-2 py-0.5 rounded text-xs font-medium ${danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
    >
      {confirmLabel}
    </button>
    <button
      onClick={onCancel}
      className="px-2 py-0.5 rounded text-xs bg-gray-600 hover:bg-gray-500 text-white"
    >
      Cancel
    </button>
  </span>
);

const RIR_OPTIONS = [
  { value: '', label: 'None' },
  { value: '0', label: '0 (Failure)' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5+' },
];

// ── Tempo Tooltip ─────────────────────────────────────────────────────────────
const TempoTooltip: React.FC = () => (
  <span className="relative group ml-1 inline-block align-middle">
    <span className="cursor-help text-gray-500 hover:text-blue-400 text-xs border border-gray-600 hover:border-blue-500 rounded-full w-4 h-4 inline-flex items-center justify-center font-semibold transition-colors">
      i
    </span>
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-xs text-gray-300 shadow-xl z-50 invisible group-hover:visible pointer-events-none leading-relaxed">
      <span className="font-semibold text-white block mb-1">Tempo format</span>
      Eccentric &ndash; Pause &ndash; Concentric
      <span className="block mt-1 text-gray-400">
        <span className="text-blue-400 font-medium">3-1-3</span> = 3s lower &middot; 1s pause &middot; 3s lift
      </span>
      <span className="block text-gray-400">
        Use <span className="text-blue-400 font-medium">X</span> for explosive/uncontrolled
      </span>
      <span className="block text-gray-400">
        Use <span className="text-blue-400 font-medium">0</span> for no pause
      </span>
      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-600" />
    </span>
  </span>
);

// ── Exercise Form ─────────────────────────────────────────────────────────────
interface ExerciseFormProps {
  planId: string; dayId: string;
  existing?: TrainingExercise;
  onSave: () => void; onCancel: () => void;
}
const ExerciseForm: React.FC<ExerciseFormProps> = ({ planId, dayId, existing, onSave, onCancel }) => {
  const [form, setForm] = useState<CreateTrainingExerciseForm>({
    name: existing?.name ?? '',
    sets: existing?.sets ?? 3,
    reps: existing?.reps ?? '8-12',
    targetRIR: existing?.targetRIR,
    tempo: existing?.tempo ?? '',
    restSeconds: existing?.restSeconds,
    notes: existing?.notes ?? '',
  });
  const [libraryId, setLibraryId] = useState<string | undefined>(
    (existing as any)?.exerciseLibraryId ?? undefined
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [library, setLibrary] = useState<ExerciseLibraryEntry[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [acOpen, setAcOpen] = useState(false);
  const [acQuery, setAcQuery] = useState('');
  const [acHighlight, setAcHighlight] = useState(-1);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<HTMLDivElement>(null);

  useEffect(() => { getLibraryCached().then(setLibrary); }, []);

  useEffect(() => {
    if (!acOpen) return;
    const handler = (e: MouseEvent) => {
      if (acRef.current && !acRef.current.contains(e.target as Node)) setAcOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [acOpen]);

  const suggestions = acQuery.trim().length > 0
    ? library.filter(e => e.name.toLowerCase().includes(acQuery.toLowerCase())).slice(0, 8)
    : [];

  // Reset highlight when the suggestion list changes
  useEffect(() => { setAcHighlight(-1); }, [suggestions.length, acQuery]);

  const selectLibraryEntry = (entry: ExerciseLibraryEntry) => {
    setForm(f => ({ ...f, name: entry.name }));
    setLibraryId(entry.exerciseLibraryId);
    setAcQuery(entry.name);
    setAcOpen(false);
  };

  const save = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Exercise name is required.'); return; }
    if (!form.sets || form.sets < 1) { setFormError('Sets must be at least 1.'); return; }
    if (!form.reps.trim()) { setFormError('Reps are required (e.g. "8-12" or "10").'); return; }
    setSaving(true);
    try {
      const payload = { ...form, exerciseLibraryId: libraryId ?? null };
      if (existing) {
        await updateExercise(planId, dayId, existing.trainingExerciseId, { ...payload, sortOrder: existing.sortOrder });
      } else {
        await addExercise(planId, dayId, payload);
      }
      onSave();
    } finally { setSaving(false); }
  };

  const field = (label: React.ReactNode, node: React.ReactNode) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-400 font-medium flex items-center">{label}</label>
      {node}
    </div>
  );
  const inp = (key: keyof CreateTrainingExerciseForm, type = 'text', extra?: object) => (
    <input
      type={type}
      value={(form[key] as string | number | undefined) ?? ''}
      onChange={e => setForm(f => ({ ...f, [key]: type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value }))}
      className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:border-blue-500 focus:outline-none"
      {...extra}
    />
  );

  return (
    <div className="bg-gray-700 rounded p-3 space-y-2 border border-gray-600">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-400 font-medium flex items-center">Exercise Name *</label>
          <div className="relative" ref={acRef}>
            <div className="flex gap-1">
              <input
                ref={nameInputRef}
                autoFocus
                type="text"
                value={acQuery || form.name}
                onChange={e => {
                  const v = e.target.value;
                  setAcQuery(v);
                  setForm(f => ({ ...f, name: v }));
                  setLibraryId(undefined);
                  setAcOpen(v.trim().length > 0);
                }}
                onFocus={() => { if ((acQuery || form.name).trim().length > 0) setAcOpen(true); }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setAcOpen(false); setAcHighlight(-1); return; }
                  if (!acOpen || suggestions.length === 0) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setAcHighlight(h => (h + 1) % suggestions.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setAcHighlight(h => (h - 1 + suggestions.length) % suggestions.length);
                  } else if (e.key === 'Enter' && acHighlight >= 0) {
                    e.preventDefault();
                    selectLibraryEntry(suggestions[acHighlight]);
                  }
                }}
                placeholder="Type or browse library…"
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:border-blue-500 focus:outline-none min-w-0"
              />
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                title="Browse exercise library"
                className="px-2 py-1 bg-gray-600 hover:bg-blue-600 text-gray-300 hover:text-white text-xs rounded border border-gray-500 hover:border-blue-500 transition-colors flex-shrink-0"
              >
                📋
              </button>
            </div>
                {acOpen && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-600 rounded-lg shadow-xl z-40 overflow-hidden">
                    {suggestions.map((s, idx) => (
                      <button
                        key={s.exerciseLibraryId}
                        type="button"
                        onMouseDown={e => { e.preventDefault(); selectLibraryEntry(s); }}
                        onMouseEnter={() => setAcHighlight(idx)}
                        className={`w-full flex items-center justify-between px-3 py-2 transition-colors text-left ${acHighlight === idx ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                      >
                        <span className="text-white text-sm truncate">{s.name}</span>
                        <span className={`text-xs ml-2 flex-shrink-0 ${acHighlight === idx ? 'text-blue-200' : 'text-gray-500'}`}>{s.muscleGroup}</span>
                      </button>
                    ))}
                  </div>
                )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium flex items-center gap-1">
              <span className={formError.includes('Sets') ? 'text-red-400' : 'text-gray-400'}>Sets *</span>
            </label>
            <input
              type="number"
              min={1}
              max={20}
              value={form.sets ?? ''}
              onChange={e => { setFormError(''); setForm(f => ({ ...f, sets: e.target.value === '' ? 0 : Number(e.target.value) })); }}
              className={`bg-gray-700 border rounded px-2 py-1 text-sm text-white focus:outline-none ${formError.includes('Sets') ? 'border-red-500 focus:border-red-400' : 'border-gray-600 focus:border-blue-500'}`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium flex items-center gap-1">
              <span className={formError.includes('Reps') ? 'text-red-400' : 'text-gray-400'}>Reps *</span>
            </label>
            <input
              type="text"
              value={form.reps}
              onChange={e => { setFormError(''); setForm(f => ({ ...f, reps: e.target.value })); }}
              placeholder="e.g. 8-12"
              className={`bg-gray-700 border rounded px-2 py-1 text-sm text-white focus:outline-none ${formError.includes('Reps') ? 'border-red-500 focus:border-red-400' : 'border-gray-600 focus:border-blue-500'}`}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {field('Target RIR', (
          <select
            value={form.targetRIR !== undefined ? String(form.targetRIR) : ''}
            onChange={e => setForm(f => ({ ...f, targetRIR: e.target.value === '' ? undefined : Number(e.target.value) }))}
            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
          >
            {RIR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
        {field(<>Tempo <TempoTooltip /></>, inp('tempo', 'text', { placeholder: '3-1-3' }))}
        {field('Rest (sec)', inp('restSeconds', 'number', { min: 0 }))}
      </div>
      {field('Coaching Notes', (
        <input type="text" value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white w-full" />
      ))}
      {formError && (
        <div className="flex items-center gap-1.5 text-red-400 text-xs bg-red-900/20 border border-red-700/40 rounded px-2.5 py-1.5">
          <span>⚠</span> {formError}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={save} disabled={saving}
          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50">
          {saving ? 'Saving…' : (existing ? 'Update' : 'Add Exercise')}
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
      </div>
      {showPicker && (
        <ExercisePicker
          library={library}
          onSelect={entry => { selectLibraryEntry(entry); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

// ── Day Card ──────────────────────────────────────────────────────────────────
interface DayCardProps {
  day: TrainingDay;
  planId: string;
  onRefresh: () => void;
  showDragHandle?: boolean;
}
const DayCard: React.FC<DayCardProps> = ({ day, planId, onRefresh, showDragHandle }) => {
  const [addingEx, setAddingEx] = useState(false);
  const [editingEx, setEditingEx] = useState<string | null>(null);
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(false);
  const [confirmDeleteEx, setConfirmDeleteEx] = useState<string | null>(null);
  const [renamingDay, setRenamingDay] = useState(false);
  const [dayNameDraft, setDayNameDraft] = useState(day.name);
  const [savingName, setSavingName] = useState(false);
  const [localExercises, setLocalExercises] = useState<TrainingExercise[]>(day.exercises);
  const dragExIdx = useRef<number | null>(null);
  const [dragOverExIdx, setDragOverExIdx] = useState<number | null>(null);

  useEffect(() => { setLocalExercises(day.exercises); }, [day.exercises]);

  const handleExDragStart = (e: React.DragEvent, idx: number) => {
    e.dataTransfer.setData('text/exercise', '1');
    e.dataTransfer.effectAllowed = 'move';
    dragExIdx.current = idx;
  };
  const handleExDragOver = (e: React.DragEvent, idx: number) => {
    if (!e.dataTransfer.types.includes('text/exercise')) return;
    e.preventDefault();
    setDragOverExIdx(idx);
  };
  const handleExDrop = (e: React.DragEvent, idx: number) => {
    if (dragExIdx.current === null) return;
    e.preventDefault();
    const from = dragExIdx.current;
    if (from === idx) { setDragOverExIdx(null); return; }
    const reordered = [...localExercises];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    setLocalExercises(reordered);
    setDragOverExIdx(null);
    dragExIdx.current = null;
    reorderExercises(planId, day.trainingDayId,
      reordered.map((ex, i) => ({ exerciseId: ex.trainingExerciseId, sortOrder: i })));
  };
  const handleExDragEnd = () => { dragExIdx.current = null; setDragOverExIdx(null); };

  const handleRenameDay = async () => {
    if (!dayNameDraft.trim() || dayNameDraft === day.name) { setRenamingDay(false); return; }
    setSavingName(true);
    try {
      await updateDay(planId, day.trainingDayId, { name: dayNameDraft.trim(), dayNumber: day.dayNumber } as any);
      onRefresh();
    } finally { setSavingName(false); setRenamingDay(false); }
  };

  const removeExercise = async (ex: TrainingExercise) => {
    await deleteExercise(planId, day.trainingDayId, ex.trainingExerciseId);
    setConfirmDeleteEx(null);
    onRefresh();
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-750">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showDragHandle && (
            <span
              className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 select-none flex-shrink-0 text-sm leading-none"
              title="Drag to reorder day"
              onMouseDown={e => e.stopPropagation()}
            >&#8942;</span>
          )}
          <span className="text-xs text-blue-400 font-semibold uppercase tracking-wide flex-shrink-0">Day {day.dayNumber}</span>
          {renamingDay ? (
            <div className="flex items-center gap-1 flex-1">
              <input
                autoFocus
                value={dayNameDraft}
                onChange={e => setDayNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRenameDay(); if (e.key === 'Escape') { setRenamingDay(false); setDayNameDraft(day.name); } }}
                className="flex-1 bg-gray-700 border border-blue-500 rounded px-2 py-0.5 text-white text-sm focus:outline-none min-w-0"
              />
              <button onClick={handleRenameDay} disabled={savingName}
                className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded disabled:opacity-50 flex-shrink-0">
                {savingName ? '…' : 'Save'}
              </button>
              <button onClick={() => { setRenamingDay(false); setDayNameDraft(day.name); }}
                className="px-2 py-0.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded flex-shrink-0">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setRenamingDay(true)}
              className="group flex items-center gap-1 text-white font-medium hover:text-blue-300 transition-colors text-left"
              title="Click to rename"
            >
              {day.name}
              <span className="text-gray-600 group-hover:text-blue-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">&#9999;</span>
            </button>
          )}
        </div>
        {!renamingDay && (
          confirmDeleteDay ? (
            <InlineConfirm
              message="Delete this day?"
              confirmLabel="Yes, Delete"
              onConfirm={async () => { await deleteDay(planId, day.trainingDayId); setConfirmDeleteDay(false); onRefresh(); }}
              onCancel={() => setConfirmDeleteDay(false)}
            />
          ) : (
            <button onClick={() => setConfirmDeleteDay(true)}
              className="text-gray-500 hover:text-red-400 text-xs px-2 py-1 flex-shrink-0">
              Delete Day
            </button>
          )
        )}
      </div>
      <div className="p-3 space-y-2">
        {localExercises.length === 0 && <p className="text-gray-500 text-sm italic">No exercises yet.</p>}
        {localExercises.map((ex, exIdx) => (
          <div
            key={ex.trainingExerciseId}
            draggable={editingEx !== ex.trainingExerciseId && confirmDeleteEx !== ex.trainingExerciseId}
            onDragStart={e => handleExDragStart(e, exIdx)}
            onDragOver={e => handleExDragOver(e, exIdx)}
            onDrop={e => handleExDrop(e, exIdx)}
            onDragEnd={handleExDragEnd}
            className={`rounded transition-all ${dragOverExIdx === exIdx ? 'ring-1 ring-inset ring-blue-500' : ''}`}
          >
            {editingEx === ex.trainingExerciseId ? (
              <ExerciseForm planId={planId} dayId={day.trainingDayId} existing={ex}
                onSave={() => { setEditingEx(null); onRefresh(); }}
                onCancel={() => setEditingEx(null)} />
            ) : (
              <div className="flex items-start justify-between bg-gray-750 rounded px-3 py-2 group">
                <span
                  className="cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400 mt-0.5 mr-2 select-none flex-shrink-0 text-sm leading-none"
                  title="Drag to reorder"
                  onMouseDown={e => e.stopPropagation()}
                >&#8942;</span>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-white text-sm font-medium">{ex.name}</span>
                    <span className="text-gray-400 text-xs">{ex.sets} &times; {ex.reps}</span>
                    {ex.targetRIR !== undefined && ex.targetRIR !== null && (
                      <span className="bg-purple-900/50 text-purple-300 text-xs px-2 py-0.5 rounded-full">
                        RIR {ex.targetRIR === 0 ? 'Failure' : ex.targetRIR}
                      </span>
                    )}
                    {ex.tempo && <span className="text-gray-500 text-xs">Tempo: {ex.tempo}</span>}
                    {ex.restSeconds && <span className="text-gray-500 text-xs">Rest: {ex.restSeconds}s</span>}
                  </div>
                  {ex.notes && <p className="text-gray-500 text-xs mt-1 italic">{ex.notes}</p>}
                  {confirmDeleteEx === ex.trainingExerciseId && (
                    <div className="mt-1">
                      <InlineConfirm
                        message={`Remove "${ex.name}"?`}
                        confirmLabel="Yes, Remove"
                        onConfirm={() => removeExercise(ex)}
                        onCancel={() => setConfirmDeleteEx(null)}
                      />
                    </div>
                  )}
                </div>
                {confirmDeleteEx !== ex.trainingExerciseId && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditingEx(ex.trainingExerciseId)} className="text-blue-400 hover:text-blue-300 text-xs px-2">Edit</button>
                    <button onClick={() => setConfirmDeleteEx(ex.trainingExerciseId)} className="text-red-400 hover:text-red-300 text-xs px-2">&#x2715;</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {addingEx ? (
          <ExerciseForm planId={planId} dayId={day.trainingDayId}
            onSave={() => { setAddingEx(false); onRefresh(); }}
            onCancel={() => setAddingEx(false)} />
        ) : (
          <button onClick={() => setAddingEx(true)}
            className="w-full text-center text-blue-400 hover:text-blue-300 text-sm py-2 border border-dashed border-gray-600 hover:border-blue-500 rounded transition-colors">
            + Add Exercise
          </button>
        )}
      </div>
    </div>
  );
};

// ── Plan Builder ──────────────────────────────────────────────────────────────
interface PlanBuilderProps { planId: string; assignmentId?: string; onBack: () => void; }
const PlanBuilder: React.FC<PlanBuilderProps> = ({ planId, assignmentId, onBack }) => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<TrainingPlanDetail | null>(null);
  const [addingDay, setAddingDay] = useState(false);
  const [newDayName, setNewDayName] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [renamingPlan, setRenamingPlan] = useState(false);
  const [planNameDraft, setPlanNameDraft] = useState('');
  const [savingPlanName, setSavingPlanName] = useState(false);
  const [localDays, setLocalDays] = useState<TrainingDay[]>([]);
  const dragDayIdx = useRef<number | null>(null);
  const [dragOverDayIdx, setDragOverDayIdx] = useState<number | null>(null);
  // Note to athlete (only used when editing an athlete copy)
  const [coachNote, setCoachNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteError, setNoteError] = useState('');

  const load = useCallback(async () => {
    const p = await getPlan(planId);
    setPlan(p);
    setLocalDays(p.days);
    setPlanNameDraft(p.name);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const handleDayDragStart = (e: React.DragEvent, idx: number) => {
    e.dataTransfer.setData('text/day', '1');
    e.dataTransfer.effectAllowed = 'move';
    dragDayIdx.current = idx;
  };
  const handleDayDragOver = (e: React.DragEvent, idx: number) => {
    if (!e.dataTransfer.types.includes('text/day')) return;
    e.preventDefault();
    setDragOverDayIdx(idx);
  };
  const handleDayDrop = (e: React.DragEvent, idx: number) => {
    if (dragDayIdx.current === null) return;
    e.preventDefault();
    const from = dragDayIdx.current;
    if (from === idx) { setDragOverDayIdx(null); return; }
    const reordered = [...localDays];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    const renumbered = reordered.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    setLocalDays(renumbered);
    setDragOverDayIdx(null);
    dragDayIdx.current = null;
    reorderDays(planId, renumbered.map((d, i) => ({ dayId: d.trainingDayId, sortOrder: i })));
  };
  const handleDayDragEnd = () => { dragDayIdx.current = null; setDragOverDayIdx(null); };

  const handleAddDay = async () => {
    if (!newDayName.trim() || !plan) return;
    const nextNum = (plan.days.length > 0 ? Math.max(...plan.days.map(d => d.dayNumber)) : 0) + 1;
    await addDay(planId, { name: newDayName.trim(), dayNumber: nextNum });
    setNewDayName(''); setAddingDay(false); load();
  };

  const handleRenamePlan = async () => {
    if (!planNameDraft.trim() || !plan) return;
    if (planNameDraft === plan.name) { setRenamingPlan(false); return; }
    setSavingPlanName(true);
    try {
      await updatePlan(planId, { name: planNameDraft.trim(), description: plan.description, isActive: plan.isActive });
      load();
    } finally { setSavingPlanName(false); setRenamingPlan(false); }
  };

  const handleSendNote = async () => {
    if (!coachNote.trim() || !assignmentId || !plan) return;
    setNoteSaving(true);
    setNoteError('');
    setNoteSaved(false);
    try {
      await updateAthletesPlan(planId, {
        name: plan.name,
        description: plan.description ?? undefined,
        notes: plan.notes ?? undefined,
        isActive: plan.isActive,
        assignmentId,
        coachNotes: coachNote.trim(),
      });
      setCoachNote('');
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 4000);
    } catch {
      setNoteError('Failed to send note. Please try again.');
    } finally {
      setNoteSaving(false);
    }
  };

  if (!plan) return <div className="p-6 text-gray-400">Loading&hellip;</div>;

  // isTemplate=false + has a templatePlanId  → coach-assigned copy being edited by coach
  // isTemplate=false + no templatePlanId     → self-coached plan being edited by the athlete
  // isTemplate=true                          → coach template
  const isAthleteCopy  = !plan.isTemplate && !!plan.templatePlanId;
  const isSelfCoached  = !plan.isTemplate && !plan.templatePlanId;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        {isAthleteCopy ? (
          <button onClick={() => navigate('/coach')} className="text-gray-400 hover:text-white text-sm flex-shrink-0">
            &larr; Back to Coach Dashboard
          </button>
        ) : isSelfCoached ? (
          <button onClick={() => navigate('/training')} className="text-gray-400 hover:text-white text-sm flex-shrink-0">
            &larr; Back to My Training
          </button>
        ) : (
          <button onClick={onBack} className="text-gray-400 hover:text-white text-sm flex-shrink-0">
            &larr; Back
          </button>
        )}
        {isAthleteCopy && (
          <span className="inline-flex items-center gap-1.5 text-xs bg-purple-900/30 border border-purple-700/40 text-purple-300 px-2.5 py-1 rounded-full">
            &#9999;&#65039; Editing athlete&apos;s plan copy
          </span>
        )}
        {isSelfCoached && (
          <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-900/30 border border-emerald-700/40 text-emerald-300 px-2.5 py-1 rounded-full">
            🏋️ My Plan
          </span>
        )}
        <div className="flex-1 min-w-0">
          {renamingPlan ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={planNameDraft}
                onChange={e => setPlanNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRenamePlan(); if (e.key === 'Escape') { setRenamingPlan(false); setPlanNameDraft(plan.name); } }}
                className="bg-gray-700 border border-blue-500 rounded px-3 py-1.5 text-white text-xl font-bold focus:outline-none w-72"
              />
              <button onClick={handleRenamePlan} disabled={savingPlanName}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50">
                {savingPlanName ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => { setRenamingPlan(false); setPlanNameDraft(plan.name); }}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setRenamingPlan(true)}
              className="group flex items-center gap-2 text-left"
              title="Click to rename plan"
            >
              <h2 className="text-xl font-bold text-white group-hover:text-blue-300 transition-colors">{plan.name}</h2>
              <span className="text-gray-500 group-hover:text-blue-400 transition-colors text-sm">&#9999;</span>
            </button>
          )}
          {plan.description && <p className="text-gray-400 text-sm">{plan.description}</p>}
        </div>
        <div className="flex items-center gap-3 text-sm flex-shrink-0">
          <span className="text-gray-500">
            {plan.days.length} days
          </span>
          {/* "Assign to Athlete" only applies to coach template plans */}
          {!isAthleteCopy && !isSelfCoached && (plan.isActive ? (
            <button onClick={() => setShowAssign(true)} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded">
              Assign to Athlete
            </button>
          ) : (
            <span className="text-xs text-amber-500 bg-amber-900/30 border border-amber-700/50 rounded px-2 py-1">
              &#9888; Unarchive this plan to assign it to athletes
            </span>
          ))}
        </div>
      </div>

      {/* Days */}
      <div className="space-y-4">
        {localDays.map((day, dayIdx) => (
          <div
            key={day.trainingDayId}
            draggable
            onDragStart={e => handleDayDragStart(e, dayIdx)}
            onDragOver={e => handleDayDragOver(e, dayIdx)}
            onDrop={e => handleDayDrop(e, dayIdx)}
            onDragEnd={handleDayDragEnd}
            className={`transition-all rounded-lg ${dragOverDayIdx === dayIdx ? 'ring-2 ring-blue-500' : ''}`}
          >
            <DayCard day={day} planId={planId} onRefresh={load} showDragHandle />
          </div>
        ))}
        {addingDay ? (
          <div className="flex gap-2">
            <input autoFocus value={newDayName} onChange={e => setNewDayName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddDay(); if (e.key === 'Escape') setAddingDay(false); }}
              placeholder="Day name, e.g. Push Day"
              className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none" />
            <button onClick={handleAddDay} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded">Add</button>
            <button onClick={() => setAddingDay(false)} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAddingDay(true)}
            className="w-full py-3 border-2 border-dashed border-gray-600 hover:border-blue-500 text-gray-400 hover:text-blue-400 rounded-lg text-sm transition-colors">
            + Add Training Day
          </button>
        )}
      </div>

      {/* Note to Athlete — only when editing an athlete's plan copy */}
      {isAthleteCopy && assignmentId && (
        <div className="bg-gray-800 border border-purple-700/40 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-purple-300 font-semibold text-sm">📝 Note to Athlete</span>
            <span className="text-gray-500 text-xs">(optional — recorded in Plan History &amp; visible to the athlete)</span>
          </div>
          <textarea
            value={coachNote}
            onChange={e => { setCoachNote(e.target.value); setNoteError(''); setNoteSaved(false); }}
            rows={3}
            placeholder="Explain what you changed and why — e.g. 'Swapped Romanian Deadlifts for Leg Curls to reduce lower back fatigue going into peak week.'"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-purple-500 focus:outline-none"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={handleSendNote}
              disabled={noteSaving || !coachNote.trim()}
              className="px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white text-sm rounded-lg font-medium disabled:opacity-50 transition-colors"
            >
              {noteSaving ? 'Sending…' : 'Send Note'}
            </button>
            {noteSaved && (
              <span className="text-green-400 text-sm flex items-center gap-1.5">
                ✓ Note sent to athlete's Plan History
              </span>
            )}
            {noteError && <span className="text-red-400 text-sm">{noteError}</span>}
          </div>
        </div>
      )}

      {showAssign && <AssignModal planId={planId} planName={plan.name} onClose={() => { setShowAssign(false); load(); }} />}
    </div>
  );
};

// ── Assign Modal ──────────────────────────────────────────────────────────────
interface AssignModalProps { planId: string; planName: string; onClose: () => void; }
const AssignModal: React.FC<AssignModalProps> = ({ planId, planName, onClose }) => {
  const [athletes, setAthletes] = useState<{ relationshipId: string; athleteUserId: string; athleteName: string }[]>([]);
  const [selected, setSelected] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successName, setSuccessName] = useState<string | null>(null);

  useEffect(() => {
    import('../../services/endpoints/coachService').then(m => m.default.getClients()).then(clients => {
      setAthletes(clients.map((c: { relationshipId: string; athleteUserId: string; athleteName: string }) => ({
        relationshipId: c.relationshipId, athleteUserId: c.athleteUserId, athleteName: c.athleteName,
      })));
    }).catch(() => {});
  }, []);

  // Auto-close 2.5s after success
  useEffect(() => {
    if (!successName) return;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [successName, onClose]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      await createAssignment({ trainingPlanId: planId, athleteUserId: selected, startDate: startDate || undefined, notes: notes || undefined });
      const athleteName = athletes.find(a => a.athleteUserId === selected)?.athleteName ?? 'the athlete';
      setSuccessName(athleteName);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const msg = err?.response?.data?.message ?? err?.message ?? '';
      if (status === 409) {
        setError(msg || 'This plan is already actively assigned to this athlete. Remove the existing assignment first.');
      } else {
        setError('Failed to assign plan. Please try again.');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md p-6">
        {successName ? (
          /* ── Success state ── */
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="w-14 h-14 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-3xl">
              ✓
            </div>
            <div>
              <p className="text-white font-semibold text-lg">Plan Assigned!</p>
              <p className="text-gray-400 text-sm mt-1">
                <span className="text-white font-medium">&ldquo;{planName}&rdquo;</span> has been assigned to{' '}
                <span className="text-green-400 font-medium">{successName}</span>.
              </p>
              <p className="text-gray-500 text-xs mt-2">They'll be notified and can start logging sessions right away.</p>
            </div>
            <button onClick={onClose} className="mt-1 px-6 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium transition-colors">
              Done
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <h3 className="text-white font-semibold mb-4">Assign &ldquo;{planName}&rdquo;</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Select Athlete *</label>
                <select value={selected} onChange={e => { setSelected(e.target.value); setError(''); }}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
                  <option value="">&mdash; choose athlete &mdash;</option>
                  {athletes.map(a => <option key={a.athleteUserId} value={a.athleteUserId}>{a.athleteName}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Start Date (optional)</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Notes (optional)</label>
                <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
              {error && (
                <div className="bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2 text-red-300 text-xs">
                  {error}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-4">
              <button onClick={submit} disabled={saving || !selected}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded disabled:opacity-50">
                {saving ? 'Assigning…' : 'Assign Plan'}
              </button>
              <button onClick={onClose} className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const TrainingPlans: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<TrainingPlanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(searchParams.get('planId'));
  const [selectedAssignmentId] = useState<string | null>(searchParams.get('assignmentId'));
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState<string | null>(null);
  const [renamingCard, setRenamingCard] = useState<string | null>(null);
  const [cardNameDraft, setCardNameDraft] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try { setPlans(await getMyPlans()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const p = await createPlan({ name: newName.trim(), description: newDesc.trim() || undefined });
      setShowCreate(false); setNewName(''); setNewDesc('');
      setSelectedPlanId(p.trainingPlanId);
      loadPlans();
    } finally { setCreating(false); }
  };

  const handleRenameCard = async (plan: TrainingPlanSummary) => {
    if (!cardNameDraft.trim() || cardNameDraft === plan.name) { setRenamingCard(null); return; }
    await updatePlan(plan.trainingPlanId, { name: cardNameDraft.trim(), isActive: plan.isActive });
    setRenamingCard(null);
    loadPlans();
  };

  if (selectedPlanId) {
    return (
      <PlanBuilder
        planId={selectedPlanId}
        assignmentId={selectedAssignmentId ?? undefined}
        onBack={() => { setSelectedPlanId(null); loadPlans(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Training Plans</h1>
          <p className="text-gray-400 text-sm mt-1">Create and manage training plans for your athletes</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium">
          + New Plan
        </button>
      </div>

      {showCreate && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-medium">New Training Plan</h3>
          <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Plan name *" onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowCreate(false); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none" />
          <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !newName.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50">
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button onClick={() => { setShowCreate(false); setNewName(''); setNewDesc(''); }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">Loading plans&hellip;</div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg mb-2">No training plans yet</p>
          <p className="text-sm">Create your first plan to start building workouts for your athletes.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active plans grid */}
          {plans.filter(p => p.isActive).length === 0 ? (
            <p className="text-gray-500 text-sm">No active plans. Create one above or unarchive an existing plan.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.filter(p => p.isActive).map(plan => (
                <div key={plan.trainingPlanId}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition-colors group flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    {renamingCard === plan.trainingPlanId ? (
                      <input autoFocus value={cardNameDraft} onChange={e => setCardNameDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameCard(plan); if (e.key === 'Escape') setRenamingCard(null); }}
                        onBlur={() => handleRenameCard(plan)}
                        className="flex-1 bg-gray-700 border border-blue-500 rounded px-2 py-1 text-white font-semibold text-sm focus:outline-none mr-2" />
                    ) : (
                      <button onClick={() => setSelectedPlanId(plan.trainingPlanId)}
                        className="flex items-center gap-1 text-left flex-1 min-w-0">
                        <h3 className="text-white font-semibold hover:text-blue-400 transition-colors truncate">{plan.name}</h3>
                      </button>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                      {renamingCard !== plan.trainingPlanId && (
                        <button onClick={() => { setRenamingCard(plan.trainingPlanId); setCardNameDraft(plan.name); setConfirmingArchive(null); }}
                          className="text-gray-600 hover:text-blue-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity px-1" title="Rename plan">&#9999;</button>
                      )}
                    </div>
                  </div>
                  {plan.description && <p className="text-gray-400 text-sm mb-3 line-clamp-2">{plan.description}</p>}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-auto pt-2">
                    <span>{plan.dayCount} day{plan.dayCount !== 1 ? 's' : ''}</span>
                    <span>{plan.assignmentCount} athlete{plan.assignmentCount !== 1 ? 's' : ''} assigned</span>
                  </div>
                  <div className="mt-3">
                    {confirmingArchive === plan.trainingPlanId ? (
                      <div className="flex items-center gap-2 py-1">
                        <InlineConfirm message="Archive this plan?" confirmLabel="Yes, Archive"
                          onConfirm={async () => { await archivePlan(plan.trainingPlanId); setConfirmingArchive(null); loadPlans(); }}
                          onCancel={() => setConfirmingArchive(null)} />
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => setSelectedPlanId(plan.trainingPlanId)}
                          className="flex-1 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white text-xs rounded transition-colors">
                          Open Builder
                        </button>
                        <button onClick={() => { setConfirmingArchive(plan.trainingPlanId); setRenamingCard(null); }}
                          className="px-3 py-1.5 bg-gray-700 hover:bg-red-900/50 text-gray-400 hover:text-red-400 text-xs rounded transition-colors">
                          Archive
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Archived plans toggle */}
          {plans.filter(p => !p.isActive).length > 0 && (
            <div>
              <button onClick={() => setShowArchived(v => !v)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors">
                <span className={`transition-transform ${showArchived ? 'rotate-90' : ''}`}>&#9654;</span>
                {showArchived ? 'Hide' : 'Show'} {plans.filter(p => !p.isActive).length} archived plan{plans.filter(p => !p.isActive).length !== 1 ? 's' : ''}
              </button>
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 opacity-70">
                  {plans.filter(p => !p.isActive).map(plan => (
                    <div key={plan.trainingPlanId}
                      className="bg-gray-800 border border-gray-700 rounded-xl p-4 group flex flex-col">
                      <div className="flex items-start justify-between mb-2">
                        {renamingCard === plan.trainingPlanId ? (
                          <input autoFocus value={cardNameDraft} onChange={e => setCardNameDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameCard(plan); if (e.key === 'Escape') setRenamingCard(null); }}
                            onBlur={() => handleRenameCard(plan)}
                            className="flex-1 bg-gray-700 border border-blue-500 rounded px-2 py-1 text-white font-semibold text-sm focus:outline-none mr-2" />
                        ) : (
                          <button onClick={() => setSelectedPlanId(plan.trainingPlanId)}
                            className="flex items-center gap-1 text-left flex-1 min-w-0">
                            <h3 className="text-gray-400 font-semibold hover:text-blue-400 transition-colors truncate">{plan.name}</h3>
                          </button>
                        )}
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          <span className="text-xs bg-gray-700 text-gray-500 px-2 py-0.5 rounded">Archived</span>
                          {renamingCard !== plan.trainingPlanId && (
                            <button onClick={() => { setRenamingCard(plan.trainingPlanId); setCardNameDraft(plan.name); }}
                              className="text-gray-600 hover:text-blue-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity px-1" title="Rename plan">&#9999;</button>
                          )}
                        </div>
                      </div>
                      {plan.description && <p className="text-gray-500 text-sm mb-3 line-clamp-2">{plan.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-gray-600 mt-auto pt-2">
                        <span>{plan.dayCount} day{plan.dayCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button onClick={() => setSelectedPlanId(plan.trainingPlanId)}
                          className="flex-1 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-white text-xs rounded transition-colors">
                          Open Builder
                        </button>
                        <button onClick={async () => { await updatePlan(plan.trainingPlanId, { name: plan.name, isActive: true }); loadPlans(); }}
                          className="flex-1 py-1.5 bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white text-xs rounded transition-colors">
                          Unarchive
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainingPlans;
